// v4 toucan — REDESIGNED. A perched toucan: a glossy dark body and head with
// a bright throat bib, the oversized banana beak (base band, thick tube,
// drooping dark tip), folded wing panels, a raised tail over a red undertail
// patch, pale eye rings and gripping feet. Two plumes: v0 the classic
// black/orange toco, v1 a green-billed variant.
//
// Ported 1:1 from age-of-mcp's v4/toucan.ts, using the library's shared
// `safari.eyes`/`safari.tube` helpers — identical to the v4 source's.
import * as THREE from "three";
import { defineModel, safari, type Part } from "../../lib";

const { eyes, tube } = safari;

const PLUMES = [
  { body: "#1c1c20", wing: "#121216", bib: "#f3efe2", beak: "#f29422",
    beakBase: "#2c1a16", beakTip: "#2c1a16", ring: "#7ec3e8",
    undertail: "#d23b2e" },
  { body: "#22221f", wing: "#161614", bib: "#f2d22e", beak: "#8fc242",
    beakBase: "#5a8c2e", beakTip: "#e07a26", ring: "#b9d24e",
    undertail: "#d23b2e" },
];

function partsFor(v: number): Part[] {
  const c = PLUMES[v % PLUMES.length]!;
  const parts: Part[] = [
    // glossy dark body and head
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.body,
      position: [-0.002, 0.034, 0], scale: [1.35, 1.1, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.body,
      position: [0.03, 0.06, 0] },
    // bright throat bib
    { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.bib,
      position: [0.026, 0.044, 0], scale: [0.95, 1.15, 0.8] },
    // the oversized banana beak: base band, thick tube, drooping dark tip
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.beakBase,
      position: [0.049, 0.0635, 0], scale: [0.28, 1.05, 1.05] },
    tube([0.047, 0.064, 0], [0.09, 0.05, 0], 0.0125, 0.0095, c.beak, 5),
    tube([0.09, 0.05, 0], [0.104, 0.04, 0], 0.0095, 0.0042, c.beakTip, 5),
    // raised tail over the red undertail patch
    { geometry: new THREE.BoxGeometry(0.048, 0.0055, 0.017), color: c.wing,
      position: [-0.047, 0.044, 0], rotation: [0, 0, 0.4] },
    { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.undertail,
      position: [-0.042, 0.036, 0], scale: [1.1, 0.8, 0.9] },
  ];
  // per side: folded wing panel, pale eye ring, gripping foot
  for (const s of [1, -1] as const) {
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.0145, 0), color: c.wing,
        position: [-0.01, 0.038, s * 0.0245], rotation: [0, s * 0.3, 0.1],
        scale: [1.35, 0.95, 0.45] },
      { geometry: new THREE.IcosahedronGeometry(0.0062, 0), color: c.ring,
        position: [0.0365, 0.0635, s * 0.0125], scale: [0.85, 1.0, 0.4] },
      { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: "#7a8fa8",
        position: [0.003, 0.0045, s * 0.0095] },
    );
  }
  parts.push(...eyes(0.0375, 0.0645, 0.0152, 0.003, "#16130e"));
  return parts;
}

export const toucanV4 = defineModel({
  id: "toucan-v4",
  batchId: "polished",
  name: "Toucan (v4)",
  tags: ["creature","toucan","low-poly","age-of-mcp","fable"],
  summary: "Low-poly toucan.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
  ],
  animations: [],
});
